import React, { useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import AppCard from './AppCard';
import TableToolbar from './TableToolbar';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const PAGINATOR_TEMPLATE = "CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink";
const PAGE_REPORT_TEMPLATE = "Showing {first} to {last} of {totalRecords} entries";

/**
 * A titled card with a search + export toolbar and a paginated DataTable.
 *
 * columns: [{ field, header, body?, sortable?, hidden? }]  (`hidden` keeps the column in exports only;
 * a column with field 'actions' or `exportable: false` renders `body` only and is left out of exports)
 * toolbarExtras: nodes shown next to the Export button; children: dialogs/popups rendered above the table.
 * Export reads the DataTable's <Column> children, so columns are rendered as a single mapped array.
 */
const DataTableCard = ({
    title,
    columns,
    rows,
    exportName,
    exportTitle,
    emptyMessage = 'No records found',
    loading = false,
    rowsPerPage = 5,
    rowsPerPageOptions = [5, 10, 25, 50],
    filters = null,
    toolbarExtras = null,
    children = null,
    exportFilename,
    toolbar = true,
    exportDisabled,
    csvOnly = false,
    searchPlaceholder,
}) => {
    const dt = useRef(null);
    const [globalFilter, setGlobalFilter] = useState('');

    const header = toolbar && (
        <TableToolbar
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            dt={dt}
            exportName={exportName ?? title}
            exportTitle={exportTitle ?? `${exportName ?? title} Report`}
            exportDisabled={exportDisabled ?? rows.length === 0}
            extras={toolbarExtras}
            csvOnly={csvOnly}
            searchPlaceholder={searchPlaceholder}
        />
    );

    return (
        <div className="w-full">
            <AppCard title={title}>
                {filters}
                {children}
                <DataTable
                    ref={dt}
                    value={rows}
                    header={header || undefined}
                    loading={loading}
                    paginator
                    rows={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    paginatorTemplate={PAGINATOR_TEMPLATE}
                    currentPageReportTemplate={PAGE_REPORT_TEMPLATE}
                    globalFilter={globalFilter}
                    exportFilename={exportFilename}
                    emptyMessage={emptyMessage}
                    tableStyle={{ minWidth: '50rem' }}
                >
                    {columns.map((col) => (col.field === 'actions' || col.exportable === false ? (
                        // Body-only columns (row actions, images) have no data field, so exports skip them.
                        <Column key={col.key ?? col.header ?? col.field} body={col.body} header={col.header} style={col.style}/>
                    ) : (
                        <Column
                            key={col.key ?? `${col.field}-${col.header}`}
                            field={col.field}
                            header={col.header}
                            body={col.body}
                            sortable={col.sortable}
                            headerClassName={col.hidden ? 'hidden' : undefined}
                            bodyClassName={col.hidden ? 'hidden' : undefined}
                        />
                    )))}
                </DataTable>
            </AppCard>
        </div>
    );
};

export default DataTableCard;
